import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import { openModal } from "../../reducers/modalReducer";
import { onIncrementValidationError } from "../../reducers/securitiesReducer";
import {
  VALIDATION_ERROR_THRESHOLD,
  shouldReportValidationPattern,
  reportValidationErrorPattern,
  addValidationBreadcrumb,
} from "../../sentry/utils";
import {
  TextField,
  getCountryPrefixOptions,
} from "@allaria-tech/matter/TextField";
import { InputSelect } from "@allaria-tech/matter/InputSelect";
import { FileData, InputFile } from "@allaria-tech/matter/InputFile";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { DatePicker } from "@allaria-tech/matter/DatePicker";
import { InputTextArea } from "@allaria-tech/matter/InputTextArea";
import { InputAddress } from "@allaria-tech/matter/InputAddress";
import { InputAmount } from "@allaria-tech/matter/InputAmount";
import { RadioButton } from "@allaria-tech/matter/RadioButton";
import { SegmentedControl } from "@allaria-tech/matter/SegmentedControl";
import { FieldLabel } from "@allaria-tech/matter/FieldLabel";
import { Checkbox } from "@allaria-tech/matter/Checkbox";
import { Fieldset } from "@allaria-tech/matter/Fieldset";
import { Button } from "@allaria-tech/matter/Button";
import {
  ConditionalBool,
  DocumentMetadata,
  Field,
  SegmentedInputOption,
  Tooltip as TooltipType,
  UploadedFile,
  ConditionalTooltip,
  InputType,
} from "../../types";
import { FormikProps, FormikValues } from "formik";
import SegmentedOptionsInput from "../SegmentedOptionsInput/SegmentedOptionsInput";
import {
  minFileSize,
  scrollElementIntoScroller,
  isSameCountry,
  utilsForLanguage,
} from "../../utils";
import {
  getIsConditionallyTrue,
  getNestedValue,
  isSchemaRequired,
} from "./utils";
import { base64ToFile, isDev, toTestId } from "../../app/utils";
import { RootState } from "../../app/store";
import { fileDragTip, inputFile, optionalLabelSuffix } from "../../i18n/inputs";
import { fileNameSpecialCharactersMessage } from "../../i18n/validationMessages";

import styles from "./TemplateInterpreter.module.scss";

interface TemplateInputSelectorProps {
  currentStepFields?: Field[];
  field: Field;
  formik: FormikProps<FormikValues>;
}

const PHONE_PREFIX_OPTIONS = getCountryPrefixOptions({
  preferred: ["AR", "ES", "UY"],
});

const PHONE_PREFIX_OPTIONS_BY_DIAL_CODE_LENGTH = [...PHONE_PREFIX_OPTIONS].sort(
  (a, b) => (b.dialCode?.length ?? 0) - (a.dialCode?.length ?? 0),
);

const matchPhonePrefix = (value: unknown) =>
  PHONE_PREFIX_OPTIONS_BY_DIAL_CODE_LENGTH.find(
    (option) =>
      option.dialCode && String(value ?? "").startsWith(option.dialCode),
  )?.value;

interface ConditionalResetAndScrollTo {
  conditionalBoolCurrentField: ConditionalBool[] | undefined;
  boolFieldsWithSameConditionalField: ConditionalBool[] | undefined;
  values: FormikValues;
  setFieldValue: (field: string, value: unknown) => void;
  scrollToElementName: (name: string, inputType: InputType) => void;
}

const conditionalResetAndScrollTo = ({
  conditionalBoolCurrentField,
  boolFieldsWithSameConditionalField,
  values,
  setFieldValue,
  scrollToElementName,
}: ConditionalResetAndScrollTo) => {
  // Scroll to conditional input to be visible
  if (conditionalBoolCurrentField && conditionalBoolCurrentField.length > 0) {
    setTimeout(() => {
      // If the current field triggers another conditional field
      for (const field of conditionalBoolCurrentField) {
        // If conditionalField have multiple boolFields, reset it only if all of them have no value
        if (
          Array.isArray(boolFieldsWithSameConditionalField) &&
          boolFieldsWithSameConditionalField?.length > 0
        ) {
          const allBoolFieldsHaveNoValue = [
            ...conditionalBoolCurrentField,
            ...boolFieldsWithSameConditionalField,
          ].every(
            (conditional) =>
              !getNestedValue(values, conditional.boolField as string),
          );

          if (allBoolFieldsHaveNoValue) {
            for (const field of boolFieldsWithSameConditionalField) {
              setFieldValue(field?.conditionalField, "");
            }
          }
        } else {
          // The conditional file is emptied
          setFieldValue(field?.conditionalField, "");
        }
      }

      if (!conditionalBoolCurrentField[0]?.dontScrollTo) {
        const { conditionalField, inputType } = conditionalBoolCurrentField[0];
        scrollToElementName(conditionalField, inputType);
      }
    }, 200);
  }
};

const scrollToElementName = (name: string, inputType?: InputType) => {
  const element =
    inputType === "file"
      ? document.getElementById(`inputFile-container-${name}`)
      : inputType === "select"
        ? document.getElementById(name)
        : document.querySelector(`[name="${name}"]`);

  element && scrollElementIntoScroller(element as HTMLElement);
};
const asTextFieldValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

// Return File from metadata
const fetchFileFromMetadata = async (meta: DocumentMetadata) => {
  const response = await fetch(meta.publicUrl);
  const blob = await response.blob();
  const file: UploadedFile = new File([blob], meta.originalFileName, {
    type: meta.contentType,
  });
  // Mark this file as already uploaded so we don't re-upload it
  file.__alreadyUploaded = true;
  return file;
};

// Compare files using unique IDs
const compareFiles = (file1: File | FileData, file2: File | FileData) => {
  if (!file1 || !file2) return false;

  // For FileData objects with id
  if ("id" in file1 && "id" in file2) {
    return file1.id === file2.id;
  }

  // For File objects, use unique ID generation
  const getFileId = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  return getFileId(file1 as File) === getFileId(file2 as File);
};

// Convert any type to File
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const anyToFile = async (item: any): Promise<File | null> => {
  // If the item is already a File, return it
  if (item instanceof File) return item;

  // If the item was fetched from the backend and is a metadata object
  // fetch the file from the public URL
  // and return it as a File object
  if (item && item.publicUrl) {
    return await fetchFileFromMetadata(item as DocumentMetadata);
  }

  // If the item is a base64 string, convert it to a File object
  // When formik values are saved to Redux during each "Continue" action,
  // File objects are converted to base64 strings because Redux does not support File objects.
  // Here, we convert those base64 strings back into File objects when retrieving the value from Redux.
  if (item && item.base64String) {
    const file: UploadedFile = base64ToFile(
      item.base64String,
      item.name,
      item.type,
    );
    file.__alreadyUploaded = true;

    return file;
  }

  return null;
};

const TemplateInputSelector = ({
  currentStepFields,
  field,
  formik,
}: TemplateInputSelectorProps) => {
  const {
    externalTooltip,
    anchorStyle,
    checkboxWrapper,
    checkboxLabel,
    checkboxGroupWrapper,
    radioGroupWrapper,
    columnsWrapper,
    inputMaxWidth,
    segmentedField,
  } = styles;

  const state = useAppSelector((state: RootState) => state);

  const {
    name,
    label: labelProp,
    hideLabel,
    conditionalTooltip,
    anchor,
    tooltip,
    type,
    dynamicType,
    options,
    selectType,
    confirmSelection,
    searchable,
    disabled,
    placeholder,
    initialValue,
    maxSize,
    validTypes,
    isNumberPercentage,
    minDatePickerDate,
    maxDatePickerDate,
    canUploadMultipleFiles,
    description,
    infoText,
    infoTextType,
    infoTextPosition,
    onChange,
    onBlur,
    conditional,
    conditionalFile,
    allConditionalsTrue,
    conditionalBool,
    autoSelect,
    country,
    conditionalValidation,
    validation,
    autoComplete,
    maxLength,
    inputMode,
    dynamicOptions,
    disabledByUrlParams,
    text,
    buttonProps,
    hideField,
    className,
    alertContent,
    disablePaste,
  } = field;

  const {
    getFieldProps,
    values,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    errors,
    touched,
    handleBlur,
  } = formik;

  const error = getNestedValue(errors, name);

  // `hideLabel` fields are labelled by a preceding label-type field, so the
  // input itself must not repeat it.
  const label = hideLabel ? "" : labelProp;

  const inputTestId = toTestId(
    typeof labelProp === "string" && labelProp ? labelProp : name,
  );

  const touch = getNestedValue(touched, name);

  const value = getNestedValue(values, name);

  const [disabledByUrl, setDisabledByUrl] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState(
    () => matchPhonePrefix(value) ?? "AR",
  );

  useEffect(() => {
    if (type !== "phone") return;
    const matched = matchPhonePrefix(value);
    if (matched && matched !== phonePrefix) setPhonePrefix(matched);
  }, [type, value, phonePrefix]);
  const fileErrorScrollTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const resolvedAddressRef = useRef<string | null>(null);

  const scrollToFileInputError = (name: string) => {
    if (fileErrorScrollTimeoutRef.current) {
      clearTimeout(fileErrorScrollTimeoutRef.current);
    }
    fileErrorScrollTimeoutRef.current = setTimeout(() => {
      const container = document.getElementById(`inputFile-container-${name}`);
      const errorEl = container?.querySelector<HTMLElement>(
        '[class*="errorMessageStyle"]',
      );
      errorEl && scrollElementIntoScroller(errorEl, "center");
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (fileErrorScrollTimeoutRef.current) {
        clearTimeout(fileErrorScrollTimeoutRef.current);
      }
    };
  }, []);

  // Track validation errors for Sentry pattern detection (securities only)
  const isSecuritiesOnboarding =
    state.activeTemplate?.onboardingType === "SECURITIES";
  const validationErrorCounts = state.securities?.validationErrorCounts || [];

  useEffect(() => {
    if (isSecuritiesOnboarding && error && touch && typeof error === "string") {
      // Dispatch to Redux to increment counter
      dispatch(
        onIncrementValidationError({ fieldName: name, errorMessage: error }),
      );

      // Check current count from state and report if threshold reached
      const existingError = validationErrorCounts.find(
        (item) => item.fieldName === name && item.errorMessage === error,
      );
      const newCount = (existingError?.count || 0) + 1;

      // Report pattern to Sentry if threshold is reached
      if (shouldReportValidationPattern(newCount, VALIDATION_ERROR_THRESHOLD)) {
        reportValidationErrorPattern(name, error, newCount, value);
      } else {
        // If threshold is not reached, add a breadcrumb for the validation error for pattern tracking
        addValidationBreadcrumb(name, error, newCount);
      }
    }
  }, [error, touch, name, isSecuritiesOnboarding]);

  // Manage uploaded files
  useEffect(() => {
    if (!value) return;

    const processFiles = async () => {
      if (canUploadMultipleFiles && Array.isArray(value)) {
        const files = (
          await Promise.all(
            value.map(async (item) => {
              return await anyToFile(item);
            }),
          )
        ).filter(Boolean) as File[];

        // Filter out files that are already uploaded
        const isSame =
          Array.isArray(value) &&
          value.length === files.length &&
          value.every((v, i) => v instanceof File && v === files[i]);

        if (!isSame) setFieldValue(name, files);
      } else if (value && !Array.isArray(value)) {
        const file = await anyToFile(value);

        if (!file || file === value) return;

        setFieldValue(name, file);
      }
    };

    processFiles();
  }, [value, canUploadMultipleFiles, name]);

  const segmentedOptions =
    type === "multiple-radio" && (options as SegmentedInputOption[]);

  const [searchParams] = useSearchParams();

  const getShouldConditionallyRender = () => {
    if (hideField) return false;

    // If the field is shown conditionally by a file
    if (conditionalFile) {
      // If the file that is conditionally needed is present or the field has a value show the field
      return !!getNestedValue(values, conditionalFile.name) || !!value;
    }

    if (conditionalBool) {
      return conditionalBool(values, dispatch, state);
    }

    // If the field is shown conditionally by multiple fields
    if (conditional) {
      return getIsConditionallyTrue(values, conditional, allConditionalsTrue);
    }

    return true;
  };

  const dispatch = useAppDispatch();
  const language = state.securities.language ?? "es";

  const getConditionalTooltip = (): TooltipType | null => {
    let tooltipToShow: TooltipType | null = null;

    const valuesMatch = (
      value: ConditionalTooltip["value"],
      conditionalValue: string,
    ) =>
      Array.isArray(value)
        ? value
            .filter((v): v is string => v !== null)
            .includes(conditionalValue)
        : value === conditionalValue;

    if (Array.isArray(conditionalTooltip)) {
      tooltipToShow =
        conditionalTooltip.find((cond) =>
          valuesMatch(cond.value, getNestedValue(values, cond.name) as string),
        )?.tooltip || null;
    } else if (conditionalTooltip) {
      const conditionalValue = getNestedValue(
        values,
        conditionalTooltip.name,
      ) as string;

      if (valuesMatch(conditionalTooltip.value, conditionalValue))
        tooltipToShow = conditionalTooltip.tooltip;
    }

    return tooltipToShow;
  };

  // To manage scroll to conditional fields
  const conditionalFieldsNames: ConditionalBool[] | undefined =
    currentStepFields
      ?.filter((field) => field.conditional || field.conditionalFile)
      .flatMap((field) => {
        if (field.conditionalFile) {
          return {
            boolField: field.conditionalFile?.name,
            conditionalField: field.name,
            dontScrollTo: Boolean(field.dontScrollTo),
            inputType: field.type,
          };
        } else if (Array.isArray(field.conditional)) {
          return field.conditional.map((conditional) => ({
            boolField: conditional.name,
            conditionalField: field.name,
            dontScrollTo: Boolean(field.dontScrollTo),
            inputType: field.type,
          }));
        }

        return {
          boolField: field.conditional?.name,
          conditionalField: field.name,
          dontScrollTo: Boolean(field.dontScrollTo),
          inputType: field.type,
        };
      });

  const conditionalBoolCurrentField = conditionalFieldsNames?.filter(
    (conditional) => conditional.boolField === name,
  );

  const boolFieldsWithSameConditionalField = conditionalFieldsNames?.filter(
    (conditional) => {
      // If the current field is not a conditional field
      if (
        !conditionalBoolCurrentField ||
        conditionalBoolCurrentField.length === 0
      ) {
        return false;
      }

      // Filter the conditional fields that have the same conditional field
      if (conditionalBoolCurrentField[0].boolField === conditional.boolField) {
        return false;
      }

      // Return only the additional conditional fields that have the same conditional field
      return (
        conditional.conditionalField ===
        conditionalBoolCurrentField[0].conditionalField
      );
    },
  );

  const filesRef = useRef<File[]>([]);
  filesRef.current = Array.isArray(value) ? (value as File[]) : [];

  const setFiles = (files: File[]) => {
    filesRef.current = files;
    setFieldValue(name, files);
  };

  const onFileUpload = (file: File) => {
    if (
      canUploadMultipleFiles &&
      filesRef.current.some(({ name }) => name === file.name)
    ) {
      setFiles([...filesRef.current]);
      showToastMessage(
        utilsForLanguage(language).fileDuplicateError(file.name),
        {
          containerId: "formAlert",
          type: "WARNING",
        },
      );
      return;
    }

    canUploadMultipleFiles
      ? setFiles([...filesRef.current, file])
      : setFieldValue(name, file);
    setFieldError(name, undefined);
    setFieldTouched(name, true, false);

    if (conditionalBoolCurrentField) {
      setTimeout(() => {
        scrollToElementName(
          conditionalBoolCurrentField[0]?.conditionalField,
          "file",
        );
      }, 200);
    }
  };

  const onFileRemove = (fileToRemove?: File | FileData) => {
    if (!fileToRemove) return;

    canUploadMultipleFiles
      ? setFiles(
          filesRef.current.filter((file) => !compareFiles(file, fileToRemove)),
        )
      : setFieldValue(name, null);

    scrollToFileInputError(name);
  };

  useEffect(() => {
    if (disabledByUrlParams) {
      // Field can be disabled by either a string or an array of strings
      if (Array.isArray(disabledByUrlParams)) {
        // Check if any parameter from the array exists in the URL
        const hasAnyParam = disabledByUrlParams.some(
          (param) => searchParams.get(param) !== null,
        );
        if (hasAnyParam) setDisabledByUrl(true);
      } else {
        const foundParam = searchParams.get(disabledByUrlParams);
        if (foundParam) setDisabledByUrl(true);
      }
    }
  }, [disabledByUrlParams, searchParams]);

  //Allow to disable the field by a function, by url params or by a boolean
  const getDisabled = () => {
    return (
      (typeof disabled === "boolean" ? disabled : disabled?.(state, values)) ||
      disabledByUrl
    );
  };

  const isInputDisabled: boolean = getDisabled();

  const getInfoText = () => {
    if (infoText === undefined) return undefined;
    return typeof infoText === "string" ? infoText : infoText(value as string);
  };

  const optionalText = optionalLabelSuffix[language];

  const isFieldOptional = (): boolean =>
    Boolean(
      (validation && !isSchemaRequired(validation)) ||
      (conditionalValidation &&
        !isSchemaRequired(conditionalValidation(state))),
    );

  const tooltipLabelAction =
    tooltip &&
    !tooltip.external &&
    !tooltip.content &&
    typeof tooltip.label === "string"
      ? {
          text: tooltip.label,
          onClick: () => {
            if (tooltip.modal) dispatch(openModal({ name: tooltip.modal }));
          },
        }
      : undefined;

  const fieldsetTooltipProps = () => {
    if (!tooltip || tooltip.external) return {};
    if (tooltip.content) {
      return { hasInfoTooltip: true, tooltipContent: tooltip.content };
    }
    if (tooltipLabelAction) return { labelAction: tooltipLabelAction };
    return {};
  };

  if (values && getShouldConditionallyRender()) {
    const inputType = dynamicType ? dynamicType(formik, state) : type;

    switch (inputType) {
      case "date":
      case "date-picker": {
        const dateValue = value as string;
        const parsedDate = dateValue
          ? moment(
              dateValue,
              [
                "YYYY-MM-DD",
                "YYYY-M-D",
                "DD/MM/YYYY",
                "D/M/YYYY",
                "DD-MM-YYYY",
                "D-M-YYYY",
              ],
              true,
            )
          : undefined;

        return (
          <DatePicker
            className={inputMaxWidth}
            name={name}
            label={label}
            testId={inputTestId}
            optional={isFieldOptional()}
            optionalText={optionalText}
            value={parsedDate?.isValid() ? parsedDate.toDate() : undefined}
            onChange={(date: Date | undefined) => {
              // "date" inputs (securities) keep the legacy DD/MM/YYYY value
              const valueFormat =
                inputType === "date" ? "DD/MM/YYYY" : "YYYY-MM-DD";
              setFieldValue(
                name,
                date ? moment(date).format(valueFormat) : null,
              );
            }}
            onBlur={() => {
              setFieldTouched(name, true, false);
            }}
            isInvalid={!!error && !!touch}
            errorMessage={error as string}
            touched={!!touch}
            disabled={isInputDisabled}
            minDate={
              minDatePickerDate ? moment(minDatePickerDate).toDate() : undefined
            }
            maxDate={
              maxDatePickerDate ? moment(maxDatePickerDate).toDate() : undefined
            }
          />
        );
      }
      case "select":
      case "combobox": {
        const rawItems =
          options && options.length > 0
            ? options
            : dynamicOptions
              ? dynamicOptions(formik, state)
              : [];

        if (!(rawItems.length > 0) && !dynamicOptions) return null;

        const selectOptions = (
          rawItems as { label: string; value: string; disabled?: boolean }[]
        ).map((option) => ({
          label: option.label,
          value: option.value,
          isDisabled: option.disabled,
        }));

        const isMultiple = !!selectType && selectType.includes("multiple");
        const isSearchable = searchable ?? inputType === "combobox";

        const runFieldOnChange = (selected: string | string[]) => {
          setFieldError(name, undefined);

          conditionalResetAndScrollTo({
            conditionalBoolCurrentField,
            boolFieldsWithSameConditionalField,
            values,
            setFieldValue,
            scrollToElementName,
          });

          const setFieldValueSkippingValidation: typeof setFieldValue = (
            fieldName,
            fieldValue,
          ) => setFieldValue(fieldName, fieldValue, false);

          if (field?.onChange) {
            field.onChange(
              values,
              setFieldValueSkippingValidation,
              setFieldError,
              setFieldTouched,
              // Select fields forward the selection here (not a DOM event);
              // matches the old `(value: any)` handler contract.
              selected as unknown as React.ChangeEvent<HTMLInputElement>,
              state,
              dispatch,
            );
          }
        };

        const sharedProps = {
          label,
          optional: isFieldOptional(),
          optionalText,
          description,
          labelAction: tooltipLabelAction,
          infoTooltip:
            tooltip && !tooltip.external ? tooltip.content : undefined,
          options: selectOptions,
          placeholder,
          name,
          searchable: isSearchable,
          isInvalid: !!error && !!touch,
          errorMessage: error as string,
          isDisabled: isInputDisabled,
          onOpenChange: (open: boolean) => {
            if (!open) setFieldTouched(name, true, false);
          },
          testId: inputTestId,
        };

        return (
          <div id={name} className={inputMaxWidth}>
            {isMultiple ? (
              <InputSelect
                key={name}
                {...sharedProps}
                multiple
                confirmSelection={confirmSelection}
                value={(value as string[]) ?? []}
                onChange={(selected) => {
                  setFieldValue(name, selected.length === 0 ? null : selected);
                  runFieldOnChange(selected);
                }}
              />
            ) : (
              <InputSelect
                key={name}
                {...sharedProps}
                value={(value as string) ?? ""}
                onChange={(selected) => {
                  setFieldValue(name, selected);
                  runFieldOnChange(selected);
                }}
              />
            )}
          </div>
        );
      }
      case "radio":
        return options && options.length > 0 ? (
          <div className={[className, inputMaxWidth].filter(Boolean).join(" ")}>
            <Fieldset
              key={name}
              label={label}
              optional={isFieldOptional()}
              optionalText={optionalText}
              {...fieldsetTooltipProps()}
            >
              <div className={radioGroupWrapper}>
                {(options as { label: string; value: string }[]).map(
                  (option) => (
                    <RadioButton
                      key={option.value}
                      name={name}
                      label={option.label}
                      testId={`${inputTestId}-${toTestId(option.label)}`}
                      value={option.value}
                      checked={value === option.value}
                      disabled={isInputDisabled}
                      onChange={(e) => {
                        onChange && onChange(values, setFieldValue);
                        formik.handleChange(e);
                        conditionalResetAndScrollTo({
                          conditionalBoolCurrentField,
                          boolFieldsWithSameConditionalField,
                          values,
                          setFieldValue,
                          scrollToElementName,
                        });
                      }}
                    />
                  ),
                )}
              </div>
            </Fieldset>
          </div>
        ) : null;
      case "segmented":
        return options && options.length > 0 ? (
          <div
            className={[className, inputMaxWidth, segmentedField]
              .filter(Boolean)
              .join(" ")}
          >
            <FieldLabel label={label} />
            <SegmentedControl
              items={(options as { label: string; value: string }[]).map(
                ({ label, value }) => ({ label, value }),
              )}
              value={typeof value === "string" ? value : undefined}
              width="fill"
              disabled={isInputDisabled}
              ariaLabel={typeof label === "string" ? label : name}
              onValueChange={(selected) => {
                setFieldError(name, undefined);
                setFieldValue(name, selected);
                onChange && onChange(values, setFieldValue);
              }}
            />
          </div>
        ) : null;
      case "phone": {
        const phoneInfoText = getInfoText();
        return (
          <TextField
            className={inputMaxWidth}
            key={name}
            name={name}
            variant="phone"
            label={label}
            testId={inputTestId}
            description={description}
            optional={isFieldOptional()}
            optionalText={optionalText}
            prefixOptions={PHONE_PREFIX_OPTIONS}
            selectedPrefix={phonePrefix}
            onPrefixChange={setPhonePrefix}
            value={asTextFieldValue(value)}
            onChange={(newValue: string) => {
              setFieldValue(name, newValue);
            }}
            onBlur={() => setFieldTouched(name, true)}
            isInvalid={!!error && !!touch}
            isValid={!!(touch && value && !error)}
            touched={!!touch}
            inputMode={inputMode}
            errorMessage={error as string}
            placeholder={placeholder}
            disabled={isInputDisabled}
            subtext={phoneInfoText ? { text: phoneInfoText } : undefined}
          />
        );
      }
      case "file":
        return (
          <InputFile
            name={name}
            key={name}
            maxSize={maxSize || 0}
            validTypes={validTypes || []}
            multiple={canUploadMultipleFiles}
            text={
              <p>
                {fileDragTip[language].dragOr}{" "}
                <span>
                  {canUploadMultipleFiles
                    ? fileDragTip[language].chooseFiles
                    : fileDragTip[language].chooseFile}
                </span>{" "}
              </p>
            }
            maxFiles={5}
            error={!!(touch && error)}
            customValidator={(file: File) => {
              const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
              const minFileSizeConvertedToBytes = minFileSize
                ? minFileSize * 1024 * 1024
                : 0;
              if (
                !isSecuritiesOnboarding &&
                !/^[a-zA-Z0-9 ]+$/.test(nameWithoutExtension)
              ) {
                scrollToFileInputError(name);
                return fileNameSpecialCharactersMessage[language ?? "es"];
              }
              if (!!file && file.size < minFileSizeConvertedToBytes) {
                scrollToFileInputError(name);
                return `${inputFile[language].minSizeErrorMessage} ${minFileSize * 1000} KB`;
              }

              return null;
            }}
            isOptional={
              (validation && !isSchemaRequired(validation)) ||
              (conditionalValidation &&
                !isSchemaRequired(conditionalValidation(state)) &&
                !conditionalFile)
            }
            optionalText={optionalText}
            // InputFile renders its own tooltip icon; it only expects the message
            tooltip={(tooltip ?? getConditionalTooltip())?.content}
            anchor={
              anchor && (
                <div className={anchorStyle} onClick={anchor.onClick}>
                  <p>{anchor.text}</p>
                </div>
              )
            }
            className={inputMaxWidth}
            label={label}
            testId={inputTestId}
            infoText={infoText as string}
            infoTextPosition={infoTextPosition}
            onFileUpload={onFileUpload}
            sizeErrorMessage={inputFile[language].sizeErrorMessage}
            formatErrorMessage={inputFile[language].formatErrorMessage}
            onFileRemove={onFileRemove}
            value={value as File}
          />
        );
      case "multiple-radio":
        return segmentedOptions && segmentedOptions.length > 0 ? (
          <div className={inputMaxWidth}>
            <SegmentedOptionsInput
              key={name}
              label={label}
              options={segmentedOptions}
              isInvalid={!touched && !value}
              {...getFieldProps(name)}
            />
          </div>
        ) : null;
      case "multiple-checkbox":
        return options && options.length > 0 ? (
          <Fieldset
            key={name}
            label={label}
            optional={isFieldOptional()}
            optionalText={optionalText}
            className={`${checkboxGroupWrapper} ${inputMaxWidth}`}
            {...fieldsetTooltipProps()}
          >
            {(options as { label: string; value: string }[]).map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                testId={`${inputTestId}-${toTestId(option.label)}`}
                checked={((value as string[]) ?? []).includes(option.value)}
                onCheckedChange={(checkedState) => {
                  const isSelected = checkedState === true;
                  setFieldValue(
                    name,
                    isSelected
                      ? [...((value as string[]) ?? []), option.value]
                      : ((value as string[]) ?? []).filter(
                          (selection) => selection !== option.value,
                        ),
                  );
                  onChange && onChange(values, setFieldValue);
                  conditionalResetAndScrollTo({
                    conditionalBoolCurrentField,
                    boolFieldsWithSameConditionalField,
                    values,
                    setFieldValue,
                    scrollToElementName,
                  });
                }}
              />
            ))}
          </Fieldset>
        ) : null;
      case "textarea":
        return (
          <InputTextArea
            className={inputMaxWidth}
            key={name}
            label={label}
            testId={inputTestId}
            optional={isFieldOptional()}
            optionalText={optionalText}
            isInvalid={!!error && !!touch}
            errorMessage={error as string}
            touched={!!touch}
            disabled={isInputDisabled}
            placeholder={placeholder}
            maxLength={maxLength}
            {...getFieldProps(name)}
          />
        );
      case "address": {
        const countryValue = country
          ? ((getNestedValue(values, country) as string | undefined) ??
            (country.includes(".") ? undefined : country))
          : undefined;
        const autoSelectValue = autoSelect
          ? ((getNestedValue(values, autoSelect) as string) ?? "")
          : "";

        const setFieldValueTrackingAddress: typeof setFieldValue = (
          field,
          fieldValue,
          shouldValidate,
        ) => {
          if (field === autoSelect) {
            resolvedAddressRef.current = String(fieldValue ?? "");
          }
          return setFieldValue(field, fieldValue, shouldValidate);
        };

        return (
          <InputAddress
            className={inputMaxWidth}
            {...getFieldProps(name)}
            onBlur={(e) => {
              handleBlur(e);
            }}
            key={name}
            label={label}
            testId={inputTestId}
            isInvalid={!!error && !!touch}
            errorMessage={error as string}
            description={getInfoText()}
            disabled={isInputDisabled}
            placeholder={placeholder}
            value={(value as string) ?? ""}
            onValueChange={async (address) => {
              if (!address) {
                if (touch) setFieldValueTrackingAddress(name, "");
                else {
                  if (autoSelect) setFieldValueTrackingAddress(name, "");
                  else setFieldValueTrackingAddress(name, value);
                }
                return;
              }
              const { formattedAddress, fullData } = address;
              const selectedCountry = countryValue;

              if (
                selectedCountry &&
                formattedAddress?.country &&
                !isSameCountry(selectedCountry, formattedAddress.country)
              ) {
                setFieldValueTrackingAddress(name, "");
                showToastMessage(
                  utilsForLanguage(language).addressCountryMismatchError(
                    selectedCountry,
                  ),
                  { containerId: "formAlert", type: "WARNING" },
                );
                return;
              }
              onChange &&
                onChange(
                  {
                    ...address,
                    formattedAddress: {
                      ...formattedAddress,
                      street:
                        formattedAddress?.street ||
                        fullData?.prediction?.structured_formatting
                          ?.main_text ||
                        fullData?.place?.name ||
                        "",
                      city:
                        formattedAddress?.city ||
                        formattedAddress?.municipality,
                    },
                  },
                  setFieldValueTrackingAddress,
                );
            }}
            country={countryValue}
            autoSelect={
              autoSelectValue === resolvedAddressRef.current
                ? undefined
                : autoSelectValue
            }
            autoComplete={autoComplete}
            onChange={formik.handleChange}
            touched={!!touch}
          />
        );
      }
      case "money":
        return (
          <InputAmount
            className={inputMaxWidth}
            name={name}
            variant="numeric"
            label={typeof label === "string" ? label : undefined}
            placeholder={placeholder}
            value={
              value === "" || value === null || value === undefined
                ? undefined
                : Number(value)
            }
            status={error && touch ? "error" : "default"}
            errorMessage={error as string}
            subtext={getInfoText()}
            disabled={isInputDisabled}
            onChange={(amount) => {
              setFieldValue(name, amount);
              onChange?.(values, setFieldValue, setFieldError, setFieldTouched);
            }}
            onBlur={(e) => {
              handleBlur(e);
              onBlur?.(values, setFieldValue, setFieldError, setFieldTouched);
            }}
          />
        );
      case "dni":
      case "cuit":
      case "text":
      case "email":
      case "number": {
        const textInfoText = getInfoText();
        const isDniCuit = type === "dni" || type === "cuit";
        const dniCuitPrefixOptions = isDniCuit
          ? [
              {
                value: type,
                label: type.toUpperCase(),
                triggerLabel: type.toUpperCase(),
              },
            ]
          : undefined;

        return (
          <div className={`${columnsWrapper} ${inputMaxWidth}`}>
            {tooltip && tooltip.external && (
              <div
                className={externalTooltip}
                onClick={() => dispatch(openModal({ name: tooltip.modal }))}
              >
                {tooltip.label}
              </div>
            )}
            <TextField
              key={name}
              name={name}
              label={label}
              testId={inputTestId}
              description={description}
              variant={
                isDniCuit
                  ? "dni-cuit"
                  : isNumberPercentage
                    ? "percentage"
                    : "default"
              }
              prefixOptions={dniCuitPrefixOptions}
              selectedPrefix={isDniCuit ? type : undefined}
              optional={isFieldOptional()}
              optionalText={optionalText}
              type={inputType as React.HTMLInputTypeAttribute}
              value={asTextFieldValue(value)}
              placeholder={placeholder}
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              touched={!!touch}
              disabled={isInputDisabled}
              maxLength={maxLength}
              inputMode={isDniCuit ? "numeric" : inputMode}
              hidePrefixWhenSingleOption
              subtext={
                textInfoText
                  ? { text: textInfoText, type: infoTextType }
                  : undefined
              }
              onChange={(newValue, element) => {
                setFieldValue(name, newValue);
                return onChange?.(
                  values,
                  setFieldValue,
                  setFieldError,
                  setFieldTouched,
                  element,
                );
              }}
              onBlur={(e) => {
                handleBlur(e);
                onBlur &&
                  onBlur(
                    values,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                    undefined,
                    state,
                  );
              }}
              {...(disablePaste &&
                !isDev && {
                  onPaste: (e) => e.preventDefault(),
                  onCut: (e) => e.preventDefault(),
                })}
              {...fieldsetTooltipProps()}
            />
          </div>
        );
      }
      case "checkbox":
        return (
          <Checkbox
            name={name}
            testId={inputTestId}
            checked={value === true || (value !== false && !!initialValue)}
            className={checkboxWrapper}
            label={
              <div className={checkboxLabel} id={`label-${name}`}>
                {label}
              </div>
            }
            onCheckedChange={(checkedState) => {
              onChange &&
                onChange(values, setFieldValue, setFieldError, setFieldTouched);
              setFieldValue(name, checkedState === true);
            }}
          />
        );

      case "button":
        return (
          <Button
            text={buttonProps?.text || ""}
            testId={toTestId(buttonProps?.text || name)}
            {...(buttonProps
              ? {
                  type: buttonProps.type,
                  variant: buttonProps.variant,
                  leadingIcon: buttonProps.startContent,
                  onClick: () => {
                    if (buttonProps?.onClick) {
                      buttonProps.onClick(values, dispatch, state);
                    }
                  },
                  isDisabled: buttonProps.disabled
                    ? buttonProps.disabled(values, state)
                    : false,
                }
              : {})}
          />
        );
      case "label":
        return <>{text}</>;
      case "alert":
        return alertContent && alertContent();
    }
  } else {
    return null;
  }
};

export default TemplateInputSelector;
